import { ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Skeleton,
  Space,
  Table,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { triggerRecompute } from '@/api/endpoints/recompute';
import RecentErrorsTable from '@/components/stats/RecentErrorsTable';
import UsageHistogramChart from '@/components/stats/UsageHistogramChart';
import { useStatsData } from '@/hooks/useStatsData';
import type { TopRoute } from '@/types/stats';
import { extractAxiosMessage } from '@/utils/axiosError';

const ROUTE_ID_PREFIX_LEN = 12;

export default function StatsPage() {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const { status, histogram, topRoutes, recentErrors, error, refresh } = useStatsData();
  const [recomputeBusy, setRecomputeBusy] = useState(false);

  const handleRecomputeAll = useCallback(() => {
    modal.confirm({
      title: t('stats.recomputeAll.confirmTitle'),
      content: t('stats.recomputeAll.confirmDescription'),
      okText: t('stats.recomputeAll.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setRecomputeBusy(true);
        try {
          await triggerRecompute();
          message.success(t('stats.recomputeAll.started'));
        } catch (err) {
          message.error(
            extractAxiosMessage(err) ?? t('stats.recomputeAll.errorTitle'),
          );
        } finally {
          setRecomputeBusy(false);
        }
      },
    });
  }, [message, modal, t]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('stats.title')}
        </Typography.Title>
        <Space size="small">
          <Button
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={status === 'loading'}
          >
            {t('stats.refresh')}
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleRecomputeAll}
            loading={recomputeBusy}
          >
            {t('stats.recomputeAll.button')}
          </Button>
        </Space>
      </Space>

      {status === 'error' && (
        <Alert
          type="error"
          showIcon
          message={t('stats.loadError')}
          description={error ?? undefined}
        />
      )}

      <Card title={t('stats.histogram.title')}>
        {status === 'loading' && !histogram ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : histogram ? (
          <UsageHistogramChart histogram={histogram} />
        ) : null}
      </Card>

      <Card title={t('stats.topRoutes.title')}>
        <TopRoutesTable items={topRoutes?.items ?? []} loading={status === 'loading'} />
      </Card>

      <Card title={t('stats.recentErrors.title')}>
        <RecentErrorsTable
          items={recentErrors?.items ?? []}
          loading={status === 'loading'}
        />
      </Card>
    </Space>
  );
}

interface TopRoutesTableProps {
  readonly items: readonly TopRoute[];
  readonly loading: boolean;
}

function TopRoutesTable({ items, loading }: TopRoutesTableProps) {
  const { t } = useTranslation();
  const columns: TableColumnsType<TopRoute> = [
    {
      title: t('stats.topRoutes.columns.route'),
      dataIndex: 'route_id',
      key: 'route_id',
      width: 140,
      render: (value: string) => (
        <Typography.Text code style={{ fontSize: 11 }}>
          {value.length > ROUTE_ID_PREFIX_LEN
            ? `${value.slice(0, ROUTE_ID_PREFIX_LEN)}…`
            : value}
        </Typography.Text>
      ),
    },
    {
      title: t('stats.topRoutes.columns.from'),
      dataIndex: 'from_address',
      ellipsis: true,
      render: (value: string | null) =>
        value ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t('stats.topRoutes.columns.to'),
      dataIndex: 'to_address',
      ellipsis: true,
      render: (value: string | null) =>
        value ?? <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t('stats.topRoutes.columns.count'),
      dataIndex: 'request_count',
      width: 100,
      align: 'right',
    },
    {
      title: t('stats.topRoutes.columns.lastUsed'),
      dataIndex: 'last_used_at',
      width: 150,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
  ];
  return (
    <Table<TopRoute>
      size="small"
      rowKey="route_id"
      columns={columns}
      dataSource={items as TopRoute[]}
      loading={loading}
      pagination={false}
    />
  );
}
